import React, { useState, useEffect } from 'react';
import { isWithinServiceArea } from '../../utils/geoUtils';
import WaitlistForm from './WaitlistForm';
import Analytics from '../../utils/analytics';

export default function LocationInput({ location, onLocationSelect }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [outOfBounds, setOutOfBounds] = useState(false);
    const [query, setQuery] = useState('');
    const [accessInstructions, setAccessInstructions] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [coordinates, setCoordinates] = useState(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (query.length > 2) {
                fetchSuggestions(query);
            } else {
                setSuggestions([]);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [query]);

    // Handle updates to parent when access instructions change
    useEffect(() => {
        if (coordinates && !outOfBounds) {
            onLocationSelect({
                lat: coordinates.lat,
                lng: coordinates.lng,
                address: query,
                accessInstructions
            });
        }
    }, [accessInstructions]);

    const fetchSuggestions = async (searchText) => {
        setLoading(true);
        try {
            // Free Nominatim API (OpenStreetMap)
            // Bounded to Canada (countrycodes=ca) to reduce noise
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&countrycodes=ca&limit=5`
            );
            const data = await response.json();
            setSuggestions(data);
        } catch (err) {
            console.error("OSM Search Error", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAddress = (place) => {
        const lat = parseFloat(place.lat);
        const lng = parseFloat(place.lon);
        const address = place.display_name;

        setQuery(address);
        setSuggestions([]);
        setCoordinates({ lat, lng });
        setError('');

        if (isWithinServiceArea(lat, lng)) {
            setOutOfBounds(false);
            onLocationSelect({
                lat,
                lng,
                address,
                accessInstructions
            });

            // Analytics: Track begin checkout since verified address is established
            Analytics.pushEcommerceEvent('begin_checkout', { location_status: 'success' });

        } else {
            setOutOfBounds(true);
            onLocationSelect(null);

            // Analytics: Track out-of-bounds interest
            Analytics.pushCustomEvent('waitlist_joined', { requested_city: address });
        }
    };

    const getUserLocation = () => {
        setLoading(true);
        setError('');
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser');
            setLoading(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                // Reverse Geocoding to get address from Lat/Lng
                let address = 'Current Location';
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
                    );
                    const data = await response.json();
                    if (data && data.display_name) {
                        address = data.display_name;
                    }
                } catch (err) {
                    console.error("Reverse Geocoding Error", err);
                }

                setCoordinates({ lat, lng });
                setQuery(address);

                if (isWithinServiceArea(lat, lng)) {
                    setOutOfBounds(false);
                    onLocationSelect({
                        lat,
                        lng,
                        address,
                        accessInstructions
                    });

                    // Analytics: Track begin checkout
                    Analytics.pushEcommerceEvent('begin_checkout', { location_status: 'success' });

                } else {
                    setOutOfBounds(true);
                    onLocationSelect(null);

                    // Analytics: Track waitlist
                    Analytics.pushCustomEvent('waitlist_joined', { requested_city: address });
                }
                setLoading(false);
            },
            (err) => {
                setError('Unable to retrieve your location');
                setLoading(false);
            }
        );
    };

    if (outOfBounds) {
        return (
            <div className="wizard-step">
                <button
                    className="btn btn-text"
                    onClick={() => {
                        setOutOfBounds(false);
                        setQuery('');
                        setCoordinates(null);
                        setSuggestions([]);
                        setAccessInstructions('');
                    }}
                >
                    ← Search another address
                </button>
                <WaitlistForm
                    address={query}
                    lat={coordinates?.lat}
                    lng={coordinates?.lng}
                    accessInstructions={accessInstructions}
                />
            </div>
        );
    }

    return (
        <div className="wizard-step">
            <h2>Where are we glowing?</h2>

            <div className="location-options">
                <button className="btn btn-secondary location-btn" onClick={getUserLocation} disabled={loading}>
                    {loading ? 'Locating...' : '📍 Use Current Location'}
                </button>

                <div className="divider"><span>OR</span></div>

                <div className="address-input-container" style={{ position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Type your address (e.g. 123 Main St, Vancouver)"
                        className="form-input location-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {suggestions.length > 0 && (
                        <ul className="location-suggestions">
                            {suggestions.map((place) => (
                                <li key={place.place_id} onClick={() => handleSelectAddress(place)}>
                                    {place.display_name}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {!loading && !error && coordinates && !outOfBounds && (
                    <div className="access-instructions-container" style={{ marginTop: '15px' }}>
                        <label className="form-label" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Access Instructions (Optional)
                        </label>
                        <input
                            type="text"
                            placeholder="Unit #, Buzzer Code, or Gate Code"
                            className="form-input"
                            value={accessInstructions}
                            onChange={(e) => setAccessInstructions(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {error && <p className="error-msg">{error}</p>}

            {location && !outOfBounds && (
                <div className="location-success">
                    <p>✓ Location set: <strong>{location.address}</strong></p>
                </div>
            )}

            {/* Simple CSS for suggestions */}
            <style>{`
                .location-suggestions {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    max-height: 200px;
                    overflow-y: auto;
                    position: absolute;
                    width: 100%;
                    background: white;
                    z-index: 1000;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .location-suggestions li {
                    padding: 10px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                }
                .location-suggestions li:hover {
                    background-color: #f0f0f0;
                }
            `}</style>
        </div>
    );
}
